// Updated File: src/pages/admin/AdminReviews.jsx
// Premium design matching ReyZa theme: Cards, icons, responsive grid, animations
// Icons: Lucide React (MessageCircle, Star, User, Trash2, ChevronLeft/Right)

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Star, User, Trash2, ChevronLeft, ChevronRight, Filter, Search } from 'lucide-react';
import { productsAPI } from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import './css/AdminReviews.css';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    productId: '',
    sort: 'createdAt'
  });

  const { fetchNotifications } = useUser();

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: 20,
        sort: filters.sort,
        ...(filters.productId && { productId: filters.productId })
      };
      const { data } = await productsAPI.getAdminReviews(params);
      setReviews(data.reviews);
      setPagination(data.pagination);
    } catch (err) {
      alert('Impossible de charger les avis');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await productsAPI.getAll();
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products for filter', err);
    }
  };

  const deleteReview = async (reviewId, productId) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await productsAPI.deleteReview(productId, reviewId);
      fetchReviews();
      fetchNotifications(); // Refresh notifications
      alert('Avis supprime');
    } catch (err) {
      alert('Echec de suppression de l\'avis');
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [filters]);

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const renderStars = (rating) => {
    return (
      <div className="ar-rating-stars">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={16} fill={i < rating ? '#BF0200' : 'none'} stroke="#BF0200" />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="ar-loading">
        <div className="ar-loading-spinner"></div>
        <p>Chargement des avis...</p>
      </div>
    );
  }

  return (
    <div className="ar-container">
      {/* Header */}
      <div className="ar-header">
        <div className="ar-header-content">
          <div className="ar-header-icon-wrapper">
            <MessageCircle size={32} className="ar-header-icon" />
          </div>
          <div>
            <h1 className="ar-title">Gestion des avis</h1>
            <p className="ar-subtitle">{pagination.total || 0} avis au total</p>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="ar-filters-card">
        <div className="ar-section-header">
          <div className="ar-section-header-content">
            <Filter size={20} className="ar-icon" />
            <h2 className="ar-section-title">Filtres</h2>
          </div>
        </div>
        <div className="ar-filters-row">
          <div className="ar-form-group">
            <label className="ar-label">Produit</label>
            <div className="ar-input-wrapper">
              <Search size={16} className="ar-input-icon" />
              <select
                value={filters.productId}
                onChange={(e) => setFilters(prev => ({ ...prev, productId: e.target.value, page: 1 }))}
                className="ar-input ar-select"
              >
                <option value="">Tous les produits</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="ar-form-group">
            <label className="ar-label">Trier par</label>
            <select
              value={filters.sort}
              onChange={(e) => setFilters(prev => ({ ...prev, sort: e.target.value, page: 1 }))}
              className="ar-input ar-select"
            >
              <option value="createdAt">Plus recent</option>
              <option value="rating">Note</option>
              <option value="updatedAt">Mis a jour recemment</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reviews Table Card */}
      <div className="ar-reviews-card">
        <div className="ar-section-header">
          <div className="ar-section-header-content">
            <User size={20} className="ar-icon" />
            <h2 className="ar-section-title">Liste des avis</h2>
            <span className="ar-items-count">Affichage {reviews.length} sur {pagination.total}</span>
          </div>
        </div>

        {reviews.length === 0 ? (
          <div className="ar-empty-state">
            <MessageCircle size={48} className="ar-empty-icon" />
            <p className="ar-empty-text">Aucun avis trouve.</p>
          </div>
        ) : (
          <>
            <div className="ar-table-wrapper">
              <table className="ar-table">
                <thead>
                  <tr>
                    <th className="ar-th-product">Produit</th>
                    <th className="ar-th-user">Utilisateur</th>
                    <th className="ar-th-rating">Note</th>
                    <th className="ar-th-comment">Commentaire</th>
                    <th className="ar-th-date">Date</th>
                    <th className="ar-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map(review => (
                    <tr key={review.id} className="ar-tr">
                      <td className="ar-td-product">
                        <Link to={`/product/${review.productId}`} className="ar-product-link">
                          {review.productName}
                        </Link>
                      </td>
                      <td className="ar-td-user">
                        <div className="ar-user-info">
                          <User size={16} className="ar-user-icon" />
                          <div>
                            <div className="ar-user-name">{review.firstName} {review.lastName}</div>
                            <div className="ar-user-email">{review.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="ar-td-rating">
                        {renderStars(review.rating)}
                        <span className="ar-rating-text">{review.rating}/5</span>
                      </td>
                      <td className="ar-td-comment">
                        {review.comment ? (
                          <span className="ar-comment-text">
                            {review.comment.length > 100 
                              ? `${review.comment.substring(0, 100)}...` 
                              : review.comment
                            }
                          </span>
                        ) : (
                          <em className="ar-no-comment">Aucun commentaire</em>
                        )}
                      </td>
                      <td className="ar-td-date">
                        <div className="ar-date-main">{new Date(review.createdAt).toLocaleDateString('fr-FR')}</div>
                        {review.updatedAt !== review.createdAt && (
                          <div className="ar-date-updated">Mis a jour</div>
                        )}
                      </td>
                      <td className="ar-td-actions">
                        <button
                          className="ar-delete-btn"
                          onClick={() => deleteReview(review.id, review.productId)}
                        >
                          <Trash2 size={16} />
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="ar-pagination">
                <button
                  className="ar-pagination-btn"
                  disabled={filters.page === 1}
                  onClick={() => handlePageChange(filters.page - 1)}
                >
                  <ChevronLeft size={16} />
                  Precedent
                </button>
                <span className="ar-pagination-info">
                  Page {filters.page} sur {pagination.pages}
                </span>
                <button
                  className="ar-pagination-btn"
                  disabled={filters.page === pagination.pages}
                  onClick={() => handlePageChange(filters.page + 1)}
                >
                  Suivant
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
